import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";

import * as BuildTeamActions from "@skills-app/build-team/actions/build-team-page.actions";
import { HomePageActions } from "@skills-app/home/Actions";
import { SkillAliasPageActions } from "@skills-app/skill-alias/actions";
import { ThingSpacePageActions } from "@skills-app/thingspace/actions";
import {
  SkillGroupApiActions,
  UpdateSkillGroupPageActions,
} from "@skills-app/skill-groups/actions";
import { ISkillGroup } from "@skills-app/models/skillGroup";

import { EMPTY } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import {
  MyProfilePageActions,
  SearchBySkillOrConsultantPageActions,
  SkillSearchBarActions,
  ViewProfilePageActions,
} from "../../consultant-skill/actions";

import { IAddSkillNodeRequest, ISkillNode } from "../../models/skillNode";
import * as NotificationActions from "../../notifications/actions/notification.actions";
import { AppState } from "../../root.reducer";
import { SkillsService } from "../../services/skills.service";

import {
  AddRelatedSkillsActions,
  DeleteSkillActions,
  EditSkillActions,
  RelatedAndAliasedSkillsActions,
  SkillApiActions,
  SkillsPageActions,
} from "../actions";

// Union type for both "select skill" actions
type SelectSkillAction =
  | ReturnType<typeof SkillsPageActions.selectSkill>
  | ReturnType<typeof SearchBySkillOrConsultantPageActions.selectSkill>;

// Normalize to a string skillId (guarding nullable Id on ISkillNode)
const getSkillIdFromSelectAction = (
  action: SelectSkillAction,
): string | null => {
  if ("skill" in action) {
    return action.skill.Id ?? null;
  }
  return action.skillId ?? null;
};

@Injectable()
export class SkillsEffects {
  constructor(
    private skillsService: SkillsService,
    private actions$: Actions,
    private store$: Store<AppState>,
  ) {}

  loadSkills$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillApiActions.addSkillError,
        SkillApiActions.editSkillError,
        SkillApiActions.deleteSkillError,
        SkillsPageActions.loadPage,
        MyProfilePageActions.loadPage,
        ViewProfilePageActions.loadPage,
        ThingSpacePageActions.loadPage,
        SearchBySkillOrConsultantPageActions.loadPage,
        SkillSearchBarActions.loadComponent,
        BuildTeamActions.loadPage,
        SkillAliasPageActions.loadPage,
        UpdateSkillGroupPageActions.loadPage,
        ThingSpacePageActions.loadPage,
        HomePageActions.loadPage,
      ),
      switchMap(() =>
        this.skillsService.getAllSkillNodes().pipe(
          map((skills: ISkillNode[]) =>
            SkillApiActions.loadSkillsSuccess({ skills }),
          ),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(SkillApiActions.loadSkillsFailure());
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  loadSkillsFilteredByRelatedSkillsThreshold$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillsPageActions.loadPage,
        MyProfilePageActions.loadPage,
        ViewProfilePageActions.loadPage,
        ThingSpacePageActions.loadPage,
        SearchBySkillOrConsultantPageActions.loadPage,
        SkillSearchBarActions.loadComponent,
        SkillAliasPageActions.loadPage,
        BuildTeamActions.loadPage,
      ),
      withLatestFrom(this.store$),
      switchMap(([, state]: [Action, AppState]) =>
        this.skillsService
          .getAllSkillNodesFilteredByRelatedSkillsThreshold(
            state.skills.relatedSkillsThreshold,
          )
          .pipe(
            map((skills: ISkillNode[]) =>
              SkillApiActions.loadSkillsByRelatedSkillsThresholdSuccess({
                skills,
              }),
            ),
            catchError((error: HttpErrorResponse) => {
              this.store$.dispatch(
                SkillApiActions.loadSkillsByRelatedSkillsThresholdFailure(),
              );
              return EMPTY;
            }),
          ),
      ),
    ),
  );

  linkSkillToSkillGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SkillApiActions.linkToSkillGroup),
      switchMap((action: { skill: ISkillNode; skillGroup: ISkillGroup }) =>
        this.skillsService
          .linkToSkillGroup(action.skill.Id ?? "", action.skillGroup.Id ?? "")
          .pipe(
            catchError((error) => {
              this.store$.dispatch(SkillApiActions.linkToSkillGroupError());
              return EMPTY;
            }),
            switchMap(() => [
              UpdateSkillGroupPageActions.selectSkillGroup({
                skillGroup: action.skillGroup,
              }),
              SkillApiActions.linkToSkillGroupSuccess(),
              NotificationActions.snackbarOpen({
                message: `Link between ${action.skill.Name} and ${action.skillGroup.Name} added successfully`,
              }),
              SkillGroupApiActions.skillGroupsForSkillNodeRequest({
                skill: action.skill,
              }),
            ]),
          ),
      ),
    ),
  );

  deleteLinkToSkillGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SkillApiActions.deleteLinkToSkillGroup),
      switchMap((action: { skill: ISkillNode; skillGroup: ISkillGroup }) =>
        this.skillsService
          .deleteLinkToSkillGroup(
            action.skill.Id ?? "",
            action.skillGroup.Id ?? "",
          )
          .pipe(
            catchError((error) => {
              this.store$.dispatch(
                SkillGroupApiActions.deleteCapabilityLinksError(),
              );
              return EMPTY;
            }),
            switchMap(() => [
              UpdateSkillGroupPageActions.selectSkillGroup({
                skillGroup: action.skillGroup,
              }),
              NotificationActions.snackbarOpen({
                message: `Link between ${action.skill.Name} and ${action.skillGroup.Name} removed successfully`,
              }),
              SkillApiActions.deleteLinkToSkillGroupSuccess(),
              SkillGroupApiActions.skillGroupsForSkillNodeRequest({
                skill: action.skill,
              }),
            ]),
          ),
      ),
    ),
  );

  loadSkillsForSelectedSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillsPageActions.selectSkill,
        SearchBySkillOrConsultantPageActions.selectSkill,
      ),
      switchMap((action: SelectSkillAction) => {
        const skillId = getSkillIdFromSelectAction(action);
        if (!skillId) return EMPTY;

        return this.skillsService.getAllSkillNodesForSearch(skillId).pipe(
          map((skills: ISkillNode[]) =>
            SkillApiActions.loadSkillsForSelectedSearchSuccess({ skills }),
          ),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(
              SkillApiActions.loadSkillsForSelectedSearchFailure(),
            );
            return EMPTY;
          }),
        );
      }),
    ),
  );

  loadRelatedSkillsBySkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillsPageActions.selectSkill,
        SearchBySkillOrConsultantPageActions.selectSkill,
      ),
      switchMap((action: SelectSkillAction) => {
        const skillId = getSkillIdFromSelectAction(action);
        if (!skillId) return EMPTY;

        return this.skillsService
          .getRelatedSkillNodesBySkillNodeId(skillId)
          .pipe(
            map((skills: ISkillNode[]) =>
              SkillApiActions.loadSelectedRelatedSkillsSuccess({ skills }),
            ),
            catchError((error: HttpErrorResponse) => {
              this.store$.dispatch(
                SkillApiActions.loadSelectedRelatedSkillsFailure(),
              );
              return EMPTY;
            }),
          );
      }),
    ),
  );

  loadRelatedSkillsFromSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillSearchBarActions.addSkillToSearch,
        RelatedAndAliasedSkillsActions.addSkillToSearch,
      ),
      switchMap((action: { skill: ISkillNode }) => {
        const skillId = action.skill.Id;
        if (!skillId) return EMPTY;

        return this.skillsService
          .getRelatedSkillNodesBySkillNodeId(skillId)
          .pipe(
            map((skills: ISkillNode[]) =>
              SkillApiActions.loadSearchRelatedSkillsSuccess({
                originalSkill: action.skill,
                relatedSkills: skills,
              }),
            ),
            catchError((error: HttpErrorResponse) => {
              this.store$.dispatch(
                SkillApiActions.loadSelectedRelatedSkillsFailure(),
              );
              return EMPTY;
            }),
          );
      }),
    ),
  );

  reloadSkillsForSelectedSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillApiActions.relatedSkillAdded,
        SkillApiActions.relatedSkillDeleted,
      ),
      withLatestFrom(this.store$),
      switchMap(([, state]: [Action, AppState]) => {
        const selectedId = state.skills.selectedSkillsId?.[0];
        if (!selectedId) return EMPTY;

        return this.skillsService.getAllSkillNodesForSearch(selectedId).pipe(
          map((skills: ISkillNode[]) =>
            SkillApiActions.loadSkillsForSelectedSearchSuccess({ skills }),
          ),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(
              SkillApiActions.loadSkillsForSelectedSearchFailure(),
            );
            return EMPTY;
          }),
        );
      }),
    ),
  );

  reloadRelatedSkillsBySkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillApiActions.relatedSkillAdded,
        SkillApiActions.relatedSkillDeleted,
      ),
      withLatestFrom(this.store$),
      switchMap(([, state]: [Action, AppState]) => {
        const selectedId = state.skills.selectedSkillsId?.[0];
        if (!selectedId) return EMPTY;

        return this.skillsService
          .getRelatedSkillNodesBySkillNodeId(selectedId)
          .pipe(
            map((skills: ISkillNode[]) =>
              SkillApiActions.loadSelectedRelatedSkillsSuccess({ skills }),
            ),
            catchError((error: HttpErrorResponse) => {
              this.store$.dispatch(
                SkillApiActions.loadSelectedRelatedSkillsFailure(),
              );
              return EMPTY;
            }),
          );
      }),
    ),
  );

  loadSkillAlisesBySkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SkillsPageActions.selectSkill,
        SearchBySkillOrConsultantPageActions.selectSkill,
      ),
      switchMap((action: SelectSkillAction) => {
        const skillId = getSkillIdFromSelectAction(action);
        if (!skillId) return EMPTY;

        return this.skillsService.getChildSkills(skillId).pipe(
          map((skills: ISkillNode[]) =>
            SkillApiActions.loadSelectedSkillAliasesSuccess({ skills }),
          ),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(
              SkillApiActions.loadSelectedSkillAliasesFailure(),
            );
            return EMPTY;
          }),
        );
      }),
    ),
  );

  addSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SkillsPageActions.requestAddSkill),
      switchMap((action: { addSkillRequest: IAddSkillNodeRequest }) =>
        this.skillsService.addSkillNode(action.addSkillRequest).pipe(
          map((createdSkill: ISkillNode) =>
            SkillApiActions.skillAdded({ createdSkill }),
          ),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(SkillApiActions.addSkillError());
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  forceAddSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SkillsPageActions.requestForceAddSkill),
      switchMap((action: { addSkillRequest: IAddSkillNodeRequest }) =>
        this.skillsService.forceAddSkillNode(action.addSkillRequest).pipe(
          map((createdSkill: ISkillNode) =>
            SkillApiActions.skillAdded({ createdSkill }),
          ),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(SkillApiActions.addSkillError());
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  editSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditSkillActions.requestEditSkill),
      switchMap((action: { skill: ISkillNode }) =>
        this.skillsService.updateSkillNode(action.skill).pipe(
          switchMap(() => [
            SkillApiActions.skillEdited(),
            NotificationActions.snackbarOpen({
              message: "Skill has been updated",
            }),
          ]),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(SkillApiActions.editSkillError());
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  forceEditSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EditSkillActions.requestForceEditSkill),
      switchMap((action: { skill: ISkillNode }) =>
        this.skillsService.forceUpdateSkillNode(action.skill).pipe(
          switchMap(() => [
            SkillApiActions.skillEdited(),
            NotificationActions.snackbarOpen({
              message: "Skill has been updated",
            }),
          ]),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(SkillApiActions.editSkillError());
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  deleteSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteSkillActions.requestDeleteSkill),
      switchMap((action: { skillId: string }) =>
        this.skillsService.deleteSkillNodeById(action.skillId).pipe(
          map((successful: boolean) => {
            if (!successful) {
              return NotificationActions.snackbarOpen({
                message:
                  "This skill is unable to be deleted. This is most likely because the skill is used in a ThingSpace.",
              });
            }
            return SkillApiActions.skillDeleted();
          }),
          catchError((error: HttpErrorResponse) => {
            this.store$.dispatch(SkillApiActions.deleteSkillError());
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  addRelatedSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddRelatedSkillsActions.requestAddRelatedSkill),
      switchMap((action: { selectedSkillId: string; relatedSkillId: string }) =>
        this.skillsService
          .addSkillEdge(action.selectedSkillId, action.relatedSkillId)
          .pipe(
            map(() => SkillApiActions.relatedSkillAdded()),
            catchError((error: HttpErrorResponse) => {
              this.store$.dispatch(SkillApiActions.addRelatedSkillFailure());
              return EMPTY;
            }),
          ),
      ),
    ),
  );

  deleteRelatedSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddRelatedSkillsActions.requestDeleteRelatedSkill),
      switchMap((action: { selectedSkillId: string; relatedSkillId: string }) =>
        this.skillsService
          .removeSkillEdge(action.selectedSkillId, action.relatedSkillId)
          .pipe(
            map(() => SkillApiActions.relatedSkillDeleted()),
            catchError((error: HttpErrorResponse) => {
              this.store$.dispatch(SkillApiActions.deleteRelatedSkillFailure());
              return EMPTY;
            }),
          ),
      ),
    ),
  );
}
